/**
 * Sixpack-stand van de catalogus (README §5.2).
 *
 * Zes losse flessen kiezen, 5% korting, statiegeld apart. De stand hangt aan
 * `?mode=sixpack` in de URL en wordt zonder herladen aan- en uitgezet, zodat
 * ingestelde filters blijven staan.
 *
 * De keuze staat in sessionStorage: je bladert door meerdere pagina's voordat
 * je zes bij elkaar hebt, en dan moet de selectie die paginawissels overleven.
 * Bewust sessionStorage en geen localStorage: een halve sixpack van vorige week
 * wil niemand terugzien. De Smokkelroute-quiz vult dezelfde sleutel ("Pas zelf
 * aan"), dus de vorm wordt bij het lezen gecontroleerd.
 *
 * Voorraad: elke kaart geeft door hoeveel er zijn (data-voorraad). De bouwer
 * stopt bij dat aantal en kijkt vóór het toevoegen ook naar wat er al in de
 * wagen zit, want Shopify weigert anders de hele sixpack met één zin
 * (onderzoek ronde 2, B19).
 *
 * De korting zelf zit NIET in deze code. Die hoort als automatische korting in
 * Shopify te staan (vanaf 6 flessen 5%), zodat de winkelwagen en de checkout
 * hetzelfde rekenen als dit balkje. Zie README §7.7. Aantal en korting komen uit
 * de thema-instellingen (regels in smokkelaar-cart.js), statiegeld per bier van
 * de kaart (data-statiegeld: blik, statiegeldfles of niets).
 *
 * Op mobiel (onder 750 px) is de balk een greepbalk van één regel en staan de
 * vakjes en de prijsopbouw in een bottom-sheet (designrapport move 4, onderzoek
 * mobiel §4). Keuze, prijzen en toevoegen lopen voor beide door dezelfde code;
 * #teken() schrijft alleen naar drie weergaven. De sheet is een <dialog> met
 * showModal(): focusval, Escape en een inerte pagina regelt de browser.
 */

import { voegToe, heeftLade, naarWinkelwagen, regels, sixpackKortingCent } from '@theme/smokkelaar-cart';
import { CollectionUpdateEvent, SearchUpdateEvent } from '@shopify/events';
import { lockScroll, unlockScroll } from '@theme/utilities';
import { DrawerOpenEvent, DrawerCloseEvent } from '@theme/theme-drawer';

const SLEUTEL = 'smokkelaar-sixpack';
const AANTAL = regels.sixpackAantal;
const SMAL = matchMedia('(max-width: 749px)');
const RUSTIG = matchMedia('(prefers-reduced-motion: reduce)');
// Zelfde duur als de transition van de sheet in smokkelaar-styles.
const SHEET_MS = 250;

const euro = (cent) => '€ ' + (cent / 100).toFixed(2).replace('.', ',');
const esc = (s) => String(s ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]);
const root = () => window.Shopify?.routes?.root || '/';
// De stijlkleur gaat in een style-attribuut: alleen een hexkleur doorlaten.
const hex = (kleur) => (/^#[0-9a-f]{3,8}$/i.test(kleur) ? kleur : '');
// Zelfde fles als snippets/smokkelaar-fles.liquid, voor een vakje zonder foto.
const fles = (kleur) =>
  `<svg class="smokkelaar-vakje__fles" viewBox="0 0 48 120" aria-hidden="true" focusable="false" style="color: ${kleur || '#8aa1b1'}"><path d="M18 0h12a1.5 1.5 0 0 1 1.5 1.5V8h-15V1.5A1.5 1.5 0 0 1 18 0z"/><path d="M19 8h10v24c0 6 11 10 11 20v58a6 6 0 0 1-6 6H14a6 6 0 0 1-6-6V52c0-10 11-14 11-20z"/></svg>`;

// Eén vakje met × om te verwijderen; in de sheet staat de naam eronder. De
// knop heeft al "<naam> verwijderen" als naam, dus de zichtbare naam is aria-hidden.
function vakje(bier, i, tekort, metNaam) {
  if (!bier) return '<li class="smokkelaar-vakje smokkelaar-vakje--leeg"></li>';
  // Zonder foto het flessilhouet in de stijlkleur, zoals op de kaart.
  const beeld = bier.beeld ? `<img src="${esc(bier.beeld)}" alt="" loading="lazy">` : fles(bier.kleur);
  const naam = metNaam ? `<span class="smokkelaar-vakje__naam" aria-hidden="true">${esc(bier.naam)}</span>` : '';
  return `<li class="smokkelaar-vakje${tekort ? ' smokkelaar-vakje--tekort' : ''}">
    <button type="button" data-verwijder="${esc(bier.variant)}" data-positie="${i}" title="${esc(bier.naam)} verwijderen">
      ${beeld}<span class="smokkelaar-vakje__kruis" aria-hidden="true">×</span>
      <span class="visually-hidden">${esc(bier.naam)} verwijderen</span>
    </button>${naam}
  </li>`;
}

function lees() {
  try {
    const ruw = sessionStorage.getItem(SLEUTEL);
    const lijst = ruw ? JSON.parse(ruw) : [];
    if (!Array.isArray(lijst)) return [];
    return lijst
      .filter((x) => x && (typeof x.variant === 'string' || typeof x.variant === 'number') && Number.isFinite(Number(x.prijs)))
      .map((x) => ({
        variant: String(x.variant),
        prijs: Number(x.prijs),
        naam: String(x.naam ?? ''),
        beeld: String(x.beeld ?? ''),
        kleur: hex(x.kleur),
        voorraad: Number.isFinite(Number(x.voorraad)) ? Number(x.voorraad) : 99,
        statiegeld: Number(x.statiegeld) || 0,
      }))
      .slice(0, AANTAL);
  } catch {
    return [];
  }
}

function schrijf(lijst) {
  try {
    sessionStorage.setItem(SLEUTEL, JSON.stringify(lijst));
  } catch {
    /* privémodus: de selectie leeft dan alleen op deze pagina */
  }
}

class Sixpack {
  #keuze = lees();
  #balk = null;
  #sheet = null;
  #delen = null;
  #fout = '';
  #bezig = false;
  #tekort = new Set();
  // Knop die de sheet opende; null = sheet dicht en opgeruimd.
  #opener = null;
  #dichtTimer = 0;
  // innerHTML leest &nbsp; en <path></path> anders terug dan we schreven; daarom
  // vergelijken met wat we zelf schreven, anders herschrijft (en herhaalt de
  // aria-live-regio) elke ververs() dezelfde inhoud.
  #getekend = new WeakMap();

  constructor() {
    this.#zetStand(new URL(location.href).searchParams.get('mode') === 'sixpack');
    // Tellers en plusknoppen op de kaarten meteen gelijk aan de bewaarde keuze
    // (na een paginawissel, of voorgevuld vanuit de Smokkelroute).
    this.ververs();
    document.addEventListener('click', this.#bijKlik);

    // Na een filter- of sorteerwissel morpht Horizon het raster: bestaande kaarten
    // verliezen hun teller, nieuwe komen erbij. Beide events lossen hun promise op
    // zodra het raster er staat; dan de tellers opnieuw zetten.
    for (const naam of [CollectionUpdateEvent.eventName, SearchUpdateEvent.eventName]) {
      document.addEventListener(naam, (event) =>
        event.promise?.then(() => {
          this.#houdModeInUrl();
          this.ververs();
        }, () => {})
      );
    }
    // "Meer laden" voegt kaarten toe zonder event. Alleen reageren op toegevoegde
    // kaartknoppen: ververs() schrijft zelf alleen attributen en tekst, dus dit
    // loopt niet in een kring (een eerdere observer deed dat wel).
    let gepland = false;
    new MutationObserver((mutaties) => {
      if (gepland) return;
      const nieuweKaart = mutaties.some((m) =>
        [...m.addedNodes].some((n) => n instanceof Element && (n.matches('.smokkelaar-sixpack-knop') || n.querySelector('.smokkelaar-sixpack-knop')))
      );
      if (!nieuweKaart) return;
      gepland = true;
      requestAnimationFrame(() => {
        gepland = false;
        this.ververs();
      });
    }).observe(document.querySelector('main') || document.body, { childList: true, subtree: true });

    // Een modale lade (winkelwagen, menu, filters op de telefoon) ligt over de balk:
    // sheet dicht en de balk inert, anders belanden focus en schermlezer achter de
    // lade (onderzoek mobiel §4). Op desktop is de winkelwagen een zijpaneel naast
    // de pagina; daar blijft de balk gewoon bruikbaar.
    for (const naam of [DrawerOpenEvent.eventName, DrawerCloseEvent.eventName]) {
      document.addEventListener(naam, () => {
        const modaal = Boolean(document.querySelector('theme-drawer[open] dialog:modal'));
        if (modaal) this.#klapIn(true);
        if (this.#balk) this.#balk.inert = modaal;
      });
    }
    // Telefoon gedraaid of venster breder: daar bestaat de sheet niet.
    SMAL.addEventListener('change', () => this.#klapIn(true));
  }

  get actief() {
    return document.body.classList.contains('smokkelaar-sixpack-actief');
  }

  // Horizon schrijft bij een filter- of sorteerwissel een nieuwe URL zonder
  // ?mode=sixpack; na herladen of Terug stond je dan weer in de losse stand
  // (QA lijst C). Horizons history.state blijft staan.
  #houdModeInUrl() {
    if (!this.actief) return;
    const url = new URL(location.href);
    if (url.searchParams.get('mode') === 'sixpack') return;
    url.searchParams.set('mode', 'sixpack');
    history.replaceState(history.state, '', url);
  }

  #zetStand(aan) {
    document.body.classList.toggle('smokkelaar-sixpack-actief', aan);
    document.querySelectorAll('[data-sixpack-stand]').forEach((knop) => {
      if (knop.classList.contains('smokkelaar-standwissel__knop')) {
        knop.setAttribute('aria-pressed', String((knop.dataset.sixpackStand === 'aan') === aan));
      }
    });

    const url = new URL(location.href);
    if (aan) url.searchParams.set('mode', 'sixpack');
    else url.searchParams.delete('mode');
    history.replaceState({}, '', url);

    if (aan) {
      this.#teken();
    } else if (this.#balk) {
      // Eerst dicht: na toevoegen vanuit de sheet wacht de winkelwagenlade op
      // het close-event van deze modale dialog (cart-drawer.js).
      this.#klapIn(true);
      this.#balk.remove();
      this.#sheet.remove();
      this.#balk = null;
      this.#sheet = null;
      this.#delen = null;
    }
  }

  #bijKlik = (event) => {
    const schakelaar = event.target.closest('[data-sixpack-stand]');
    if (schakelaar) {
      event.preventDefault();
      this.#zetStand(schakelaar.dataset.sixpackStand === 'aan');
      this.ververs();
      return;
    }

    if (!this.actief) return;

    const knop = event.target.closest('[data-actie]');
    if (!knop) return;
    const houder = knop.closest('.smokkelaar-sixpack-knop');
    if (!houder) return;

    event.preventDefault();
    const variant = houder.dataset.variant;

    if (knop.dataset.actie === 'voeg-toe') {
      if (this.#keuze.length >= AANTAL) return;
      const voorraad = Number(houder.dataset.voorraad) || 99;
      if (this.#keuze.filter((x) => x.variant === variant).length >= voorraad) return;
      this.#keuze.push({
        variant,
        prijs: Number(houder.dataset.prijs) || 0,
        naam: houder.dataset.naam || '',
        beeld: houder.dataset.beeld || '',
        kleur: hex(houder.dataset.kleur),
        voorraad,
        statiegeld: Number(houder.dataset.statiegeld) || 0,
      });
    } else {
      const i = this.#keuze.map((x) => x.variant).lastIndexOf(variant);
      if (i > -1) this.#keuze.splice(i, 1);
    }

    this.#fout = '';
    this.#tekort.clear();
    schrijf(this.#keuze);
    this.ververs();
  };

  ververs() {
    // De schakelaar komt na een filterwissel opnieuw uit Liquid, en die ziet
    // ?mode=sixpack niet: aria-pressed weer gelijk aan de stand.
    document.querySelectorAll('.smokkelaar-standwissel__knop').forEach((knop) => {
      const waarde = String((knop.dataset.sixpackStand === 'aan') === this.actief);
      if (knop.getAttribute('aria-pressed') !== waarde) knop.setAttribute('aria-pressed', waarde);
    });
    // Aantallen op de kaarten. Alleen schrijven als er iets verandert: een
    // blinde schrijf zou Horizons observers voor niets laten afgaan.
    document.querySelectorAll('.smokkelaar-sixpack-knop').forEach((houder) => {
      const n = this.#keuze.filter((x) => x.variant === houder.dataset.variant).length;
      const waarde = n ? String(n) : '';
      if (houder.dataset.gekozen !== waarde) houder.dataset.gekozen = waarde;
      const teller = houder.querySelector('.smokkelaar-sixpack-knop__aantal');
      if (teller && teller.textContent !== waarde) teller.textContent = waarde;
      // Op is op: de plusknop stopt bij de voorraad.
      const voorraad = Number(houder.dataset.voorraad) || 99;
      const plus = houder.querySelector('[data-actie="voeg-toe"]');
      const vol = n >= voorraad;
      if (plus && plus.disabled !== vol) {
        plus.disabled = vol;
        plus.title = vol ? `Meer ${houder.dataset.naam || 'flessen'} is er niet op voorraad` : '';
      }
    });
    if (this.actief) this.#teken();
  }

  /**
   * Bouwt balk en sheet één keer; daarna worden alleen de delen bijgewerkt (aria-live werkt dan echt).
   * De balk heeft twee weergaven: __binnen vanaf 750 px, __mini daaronder (CSS kiest).
   */
  #bouw() {
    const balk = document.createElement('div');
    balk.className = 'smokkelaar-sixpackbalk';
    balk.setAttribute('role', 'region');
    balk.setAttribute('aria-label', 'Jouw sixpack');
    balk.innerHTML = `
      <div class="smokkelaar-sixpackbalk__binnen">
        <ul class="smokkelaar-sixpackbalk__vakjes" data-vakjes></ul>
        <div class="smokkelaar-sixpackbalk__som" aria-live="polite">
          <p class="smokkelaar-sixpackbalk__prijs" data-prijs></p>
          <p class="smokkelaar-sixpackbalk__meta" data-meta></p>
          <p class="smokkelaar-sixpackbalk__fout" data-fout hidden></p>
        </div>
        <button type="button" class="smokkelaar-sixpackbalk__cta" data-sixpack-in-wagen disabled></button>
        <button type="button" class="smokkelaar-sixpackbalk__sluiten" data-sixpack-stand="uit" aria-label="Sixpack samenstellen sluiten">×</button>
      </div>
      <div class="smokkelaar-sixpackbalk__mini">
        <button type="button" class="smokkelaar-sixpackbalk__overzicht" data-sheet-open aria-expanded="false" aria-controls="smokkelaar-sixpacksheet">
          <span class="smokkelaar-sixpackbalk__voortgang">
            <span data-teller></span>
            <span class="smokkelaar-sixpackbalk__stippen" data-stippen aria-hidden="true"></span>
          </span>
          <span class="smokkelaar-sixpackbalk__miniprijs" data-miniprijs></span>
        </button>
        <button type="button" class="smokkelaar-sixpackbalk__actie" data-sheet-open aria-expanded="false" aria-controls="smokkelaar-sixpacksheet">Bekijk</button>
        <button type="button" class="smokkelaar-sixpackbalk__actie smokkelaar-sixpackbalk__actie--wagen" data-sixpack-in-wagen hidden></button>
        <p class="visually-hidden" data-status aria-live="polite"></p>
      </div>`;

    const sheet = document.createElement('dialog');
    sheet.className = 'smokkelaar-sixpacksheet';
    sheet.id = 'smokkelaar-sixpacksheet';
    sheet.setAttribute('aria-labelledby', 'smokkelaar-sixpacksheet-kop');
    sheet.setAttribute('aria-describedby', 'smokkelaar-sixpacksheet-fout');
    sheet.innerHTML = `
      <div class="smokkelaar-sixpacksheet__paneel">
        <div class="smokkelaar-sixpacksheet__greep" data-greep aria-hidden="true"></div>
        <div class="smokkelaar-sixpacksheet__kop">
          <h2 id="smokkelaar-sixpacksheet-kop" tabindex="-1">Jouw sixpack</h2>
          <p data-sheet-teller></p>
        </div>
        <div class="smokkelaar-sixpacksheet__inhoud">
          <ul class="smokkelaar-sixpacksheet__vakjes" data-sheet-vakjes></ul>
          <dl class="smokkelaar-sixpacksheet__som" data-sheet-som></dl>
          <p class="smokkelaar-sixpackbalk__fout" id="smokkelaar-sixpacksheet-fout" role="alert" data-sheet-fout hidden></p>
        </div>
        <div class="smokkelaar-sixpacksheet__voet">
          <button type="button" class="smokkelaar-sixpacksheet__verder" data-sheet-dicht>Verder kiezen</button>
          <button type="button" class="smokkelaar-sixpackbalk__cta" data-sixpack-in-wagen disabled></button>
        </div>
      </div>`;

    document.body.append(balk, sheet);
    // De pagina krijgt precies de hoogte van de balk als onderruimte (CSS leest de variabele);
    // op mobiel is dat de ingeklapte balk, de sheet ligt in de top layer.
    new ResizeObserver(() => document.body.style.setProperty('--sixpackbalk-hoogte', `${balk.offsetHeight}px`)).observe(balk);

    balk.addEventListener('click', this.#bijBalkKlik);
    sheet.addEventListener('click', this.#bijBalkKlik);
    // Escape: zelf inklappen, dan schuift de sheet net zo weg als hij kwam.
    sheet.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.#klapIn();
    });
    // Sluit de browser de dialog zelf (zoals de terugknop op Android), dan ook opruimen.
    sheet.addEventListener('close', () => this.#naSluiten());
    this.#sleepOmlaag(sheet.querySelector('[data-greep]'), sheet.querySelector('.smokkelaar-sixpacksheet__paneel'));

    this.#balk = balk;
    this.#sheet = sheet;
    const deel = (selector) => balk.querySelector(selector) || sheet.querySelector(selector);
    this.#delen = {
      vakjes: deel('[data-vakjes]'),
      prijs: deel('[data-prijs]'),
      meta: deel('[data-meta]'),
      fout: deel('[data-fout]'),
      cta: deel('[data-sixpack-in-wagen]'),
      teller: deel('[data-teller]'),
      stippen: deel('[data-stippen]'),
      miniprijs: deel('[data-miniprijs]'),
      status: deel('[data-status]'),
      bekijk: deel('.smokkelaar-sixpackbalk__actie[data-sheet-open]'),
      inWagen: deel('.smokkelaar-sixpackbalk__actie--wagen'),
      sheetTeller: deel('[data-sheet-teller]'),
      sheetVakjes: deel('[data-sheet-vakjes]'),
      som: deel('[data-sheet-som]'),
      sheetFout: deel('[data-sheet-fout]'),
      sheetCta: sheet.querySelector('[data-sixpack-in-wagen]'),
    };
  }

  #bijBalkKlik = (event) => {
    const houder = event.currentTarget;
    // Tik naast het paneel = op de donkere achtergrond (de dialog zelf).
    if (event.target === this.#sheet) return this.#klapIn();
    const knop = event.target.closest('[data-verwijder], [data-sixpack-in-wagen], [data-sheet-open], [data-sheet-dicht]');
    if (!knop) return;
    if (knop.hasAttribute('data-sixpack-in-wagen')) return this.#naarWinkelwagen(knop);
    if (knop.hasAttribute('data-sheet-open')) return this.#klapUit(knop);
    if (knop.hasAttribute('data-sheet-dicht')) return this.#klapIn();

    const positie = Number(knop.dataset.positie);
    const i = this.#keuze.map((x) => x.variant).lastIndexOf(knop.dataset.verwijder);
    if (i > -1) this.#keuze.splice(i, 1);
    this.#fout = '';
    this.#tekort.clear();
    schrijf(this.#keuze);
    this.ververs();
    // Focus op het volgende vakje, of anders op een knop: niet naar <body>.
    // In de sheet is dat "Verder kiezen", in de brede balk de knop naar de wagen.
    const vakjes = houder.querySelectorAll('[data-verwijder]');
    (vakjes[Math.min(positie, vakjes.length - 1)] || houder.querySelector('[data-sheet-dicht], [data-sixpack-in-wagen]'))?.focus();
  };

  #klapUit(opener) {
    const sheet = this.#sheet;
    if (!sheet || sheet.open) return;
    clearTimeout(this.#dichtTimer);
    this.#opener = opener;
    sheet.showModal();
    // Horizons scroll-lock: zonder dit scrolt de pagina op iOS achter de sheet mee.
    lockScroll(sheet);
    this.#balk.querySelectorAll('[data-sheet-open]').forEach((k) => k.setAttribute('aria-expanded', 'true'));
    sheet.querySelector('h2').focus({ preventScroll: true });
    // Eerst de dichte stand laten berekenen, dan pas de klasse: anders is er geen overgang.
    sheet.getBoundingClientRect();
    sheet.classList.add('is-open');
  }

  /** Schuift de sheet weg en sluit de dialog daarna; `direct` sluit meteen (lade gaat open, stand dicht). */
  #klapIn(direct = false) {
    const sheet = this.#sheet;
    if (!sheet?.open) return;
    sheet.classList.remove('is-open');
    clearTimeout(this.#dichtTimer);
    const dicht = () => {
      sheet.close();
      this.#naSluiten();
    };
    if (direct || RUSTIG.matches) dicht();
    else this.#dichtTimer = setTimeout(dicht, SHEET_MS);
  }

  // Eén keer opruimen per keer open: direct na ons eigen close(), of via het
  // close-event als de browser sloot. Dat event komt als losse taak (soms pas een
  // frame later), dus niets doen als de sheet intussen weer open is.
  #naSluiten() {
    const sheet = this.#sheet;
    const opener = this.#opener;
    if (!sheet || sheet.open || !opener) return;
    this.#opener = null;
    sheet.classList.remove('is-open');
    unlockScroll(sheet);
    const openers = [...this.#balk.querySelectorAll('[data-sheet-open]')];
    openers.forEach((k) => k.setAttribute('aria-expanded', 'false'));
    // Focus terug naar de knop die opende; staat die er niet meer (vol → "In winkelwagen"),
    // dan een zichtbare openknop. Na toevoegen verdwijnt de balk en neemt de lade de focus.
    [opener, ...openers].find((k) => k.isConnected && k.offsetParent)?.focus();
  }

  /** Greep omlaag vegen: het paneel volgt de vinger; ver genoeg is inklappen, anders veert hij terug. */
  #sleepOmlaag(greep, paneel) {
    greep.addEventListener('pointerdown', (event) => {
      const start = event.clientY;
      let omlaag = 0;
      const beweeg = (e) => {
        omlaag = Math.max(0, e.clientY - start);
        paneel.style.transform = `translateY(${omlaag}px)`;
      };
      greep.setPointerCapture(event.pointerId);
      paneel.style.transition = 'none';
      greep.addEventListener('pointermove', beweeg);
      // Komt na pointerup én pointercancel, dus één plek om op te ruimen.
      greep.addEventListener(
        'lostpointercapture',
        () => {
          greep.removeEventListener('pointermove', beweeg);
          paneel.style.transition = '';
          paneel.style.transform = '';
          if (omlaag > 60) this.#klapIn();
        },
        { once: true }
      );
    });
  }

  #teken() {
    if (!this.#balk) this.#bouw();
    const d = this.#delen;

    const gekozen = this.#keuze;
    const n = gekozen.length;
    const los = gekozen.reduce((s, x) => s + x.prijs, 0);
    // Shopify berekent een procentuele korting PER REGEL en kapt daarbij af,
    // niet in een keer over het totaal. Zelfde formule hier, anders belooft
    // deze balk een ander bedrag dan de winkelwagen rekent. Gemeten verschil
    // op een voorbeeldsixpack: 3 cent.
    const kortingCent = sixpackKortingCent(gekozen.map((x) => x.prijs));
    const metKorting = los - kortingCent;
    // Per bier: blik, statiegeldfles of niets (smokkelaar-statiegeld-cent.liquid).
    const statiegeld = gekozen.reduce((s, x) => s + x.statiegeld, 0);
    const inclStatiegeld = statiegeld > 0 ? ' incl. statiegeld' : '';
    const totaal = metKorting + statiegeld;
    const compleet = n === AANTAL;
    const rest = AANTAL - n;
    const nogKiezen = `Kies nog ${rest} ${rest === 1 ? 'bier' : 'bieren'}`;
    const vakjes = (metNaam) =>
      Array.from({ length: AANTAL }, (_, i) => vakje(gekozen[i], i, this.#tekort.has(gekozen[i]?.variant), metNaam)).join('');

    // Brede balk (vanaf 750 px)
    this.#zet(d.vakjes, vakjes(false));
    const prijs = los > 0
      ? `<s>${euro(los)}</s> <b>${euro(metKorting)}</b> <em>&minus;${regels.sixpackKorting}%</em>`
      : '<b>Nog niets gekozen</b>';
    this.#zet(d.prijs, prijs);
    this.#tekst(d.meta, statiegeld > 0 ? `+ ${euro(statiegeld)} statiegeld` : '');
    // De knop noemt wat je echt betaalt: sixpack plus statiegeld.
    this.#zet(d.cta, this.#bezig ? 'Bezig…' : compleet ? `Zet sixpack in winkelwagen · ${euro(totaal)}${inclStatiegeld}` : nogKiezen);

    // Ingeklapte balk (mobiel): voortgang, prijs, en één knop die meegroeit met de keuze.
    this.#tekst(d.teller, `${n} van ${AANTAL}`);
    this.#zet(d.stippen, Array.from({ length: AANTAL }, (_, i) => (i < n ? '<span class="is-vol"></span>' : '<span></span>')).join(''));
    this.#zet(
      d.miniprijs,
      los > 0 ? `<b>${euro(metKorting)}</b><small>met ${regels.sixpackKorting}% korting</small>` : `<b>Nog leeg</b><small>kies ${AANTAL} bieren</small>`
    );
    this.#tekst(d.status, `${n} van ${AANTAL} gekozen${los > 0 ? `, ${euro(metKorting)}` : ''}`);
    if (d.bekijk.hidden !== compleet) d.bekijk.hidden = compleet;
    if (d.inWagen.hidden !== !compleet) d.inWagen.hidden = !compleet;
    this.#tekst(d.inWagen, this.#bezig ? 'Bezig…' : 'In winkelwagen');

    // Sheet (mobiel): grote vakjes met naam en de hele prijsopbouw.
    this.#tekst(d.sheetTeller, `${n} van ${AANTAL} gekozen`);
    this.#zet(d.sheetVakjes, vakjes(true));
    const regel = (term, bedrag) => `<div><dt>${term}</dt><dd>${bedrag}</dd></div>`;
    this.#zet(
      d.som,
      los > 0
        ? regel(`Los (${n} ${n === 1 ? 'fles' : 'flessen'})`, euro(los)) +
            regel(`Sixpackkorting ${regels.sixpackKorting}%`, `&minus; ${euro(kortingCent)}`) +
            (statiegeld > 0 ? regel('Statiegeld', `+ ${euro(statiegeld)}`) : '') +
            regel('Totaal', euro(totaal))
        : ''
    );
    d.som.hidden = los === 0;
    this.#zet(d.sheetCta, this.#bezig ? 'Bezig…' : compleet ? `Zet in winkelwagen<small>${euro(totaal)}${inclStatiegeld}</small>` : nogKiezen);

    for (const fout of [d.fout, d.sheetFout]) {
      this.#tekst(fout, this.#fout);
      fout.hidden = !this.#fout;
    }
    for (const knop of [d.cta, d.inWagen, d.sheetCta]) knop.disabled = this.#bezig || !compleet;
  }

  #zet(el, html) {
    if (this.#getekend.get(el) === html) return;
    this.#getekend.set(el, html);
    el.innerHTML = html;
  }

  // Zelfde tekst niet opnieuw schrijven: een live-regio zou hem anders herhalen.
  #tekst(el, tekst) {
    if (el.textContent !== tekst) el.textContent = tekst;
  }

  /** Welke gekozen bieren passen niet bij de voorraad, met wat al in de wagen zit? */
  async #controleerVoorraad() {
    let inWagen = new Map();
    try {
      const wagen = await fetch(`${root()}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
      for (const i of wagen.items) inWagen.set(String(i.variant_id), (inWagen.get(String(i.variant_id)) || 0) + i.quantity);
    } catch {
      inWagen = new Map();
    }
    const perVariant = new Map();
    for (const x of this.#keuze) perVariant.set(x.variant, (perVariant.get(x.variant) || 0) + 1);
    const tekort = [];
    for (const [variant, n] of perVariant) {
      const bier = this.#keuze.find((x) => x.variant === variant);
      const over = Math.max(0, bier.voorraad - (inWagen.get(variant) || 0));
      if (n > over) tekort.push({ ...bier, over });
    }
    return tekort;
  }

  /** @param {HTMLButtonElement} bron  de knop die toevoegt (brede balk, greepbalk of sheet) */
  async #naarWinkelwagen(bron) {
    if (this.#bezig) return;
    this.#bezig = true;
    this.#fout = '';
    this.#tekort.clear();
    this.#teken();

    const tekort = await this.#controleerVoorraad();
    if (tekort.length) {
      this.#fout = tekort
        .map((t) =>
          t.over === 0
            ? `${t.naam} is op, of zit al in je winkelwagen. Haal het uit je sixpack en kies iets anders.`
            : `Van ${t.naam} ${t.over === 1 ? 'is er nog maar 1' : `zijn er nog maar ${t.over}`}. Haal er een uit je sixpack en kies iets anders.`
        )
        .join(' ');
      tekort.forEach((t) => this.#tekort.add(t.variant));
      this.#mislukt(bron);
      return;
    }

    try {
      await voegToe(this.#keuze.map((x) => ({ id: x.variant, quantity: 1 })), { bron });
      this.#bezig = false;
      this.#keuze = [];
      schrijf(this.#keuze);
      if (heeftLade()) {
        // De lade gaat vanzelf open (auto-open). Stand dicht (sluit ook de sheet), tellers op de kaarten op nul.
        this.#zetStand(false);
        this.ververs();
      } else {
        naarWinkelwagen();
      }
    } catch (fout) {
      console.warn('[smokkelaar-sixpack]', fout);
      // Shopify zegt zelf wat er mis is ("De maximale hoeveelheid…"); de keuze blijft staan.
      this.#fout = `${fout?.message ? fout.message + ' ' : 'Dat lukte niet. '}Pas je sixpack aan en probeer het nog eens.`;
      this.#mislukt(bron);
    }
  }

  // Op mobiel staat de melding in de sheet, naast de vakjes met het rode randje:
  // kwam de klik uit de greepbalk, dan gaat de sheet open.
  #mislukt(bron) {
    this.#bezig = false;
    this.#teken();
    if (SMAL.matches && !this.#sheet.open) this.#klapUit(bron);
  }
}

new Sixpack();
